import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
    Search, FileText, LayoutTemplate, Briefcase, Users, Navigation, ExternalLink, 
    MessageSquare, MapPin, UserCheck, CheckCircle, Building, Calendar, Clock, 
    UserPlus, DollarSign, Activity, BookOpen, Settings, Shield, Star, Mail, Phone,
    Sparkles, HelpCircle, Bot, Megaphone, Bell, BarChart3, Zap, Cake
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
    actionShortcuts, 
    coreModules, 
    documentationItems, 
    smartSuggestionChips 
} from '../config/quickSearchCatalog';

// Chuẩn hóa tiếng Việt bỏ dấu (hỗ trợ cả chữ đ/Đ)
const removeVietnameseTones = (str) => {
    if (!str) return '';
    return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[đĐ]/g, 'd')
        .toLowerCase()
        .trim();
};

const CommandPalette = ({ onNavigate }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [dynamicDocs, setDynamicDocs] = useState([]);
    const [recentSearches, setRecentSearches] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('cmdPaletteRecent') || '[]');
        } catch { return []; }
    });
    
    const inputRef = useRef(null);
    const navigate = useNavigate();

    // Lấy thông tin user hiện tại để phân quyền kết quả tìm kiếm
    const currentUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user') || '{}');
        } catch {
            return {};
        }
    }, [isOpen]);

    // Fetch dynamic licenses & announcements
    useEffect(() => {
        const fetchRemoteDocs = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const [licRes, annRes] = await Promise.allSettled([
                    axios.get('/api/licenses', { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get('/api/announcements', { headers: { Authorization: `Bearer ${token}` } })
                ]);
                
                const docs = [];
                if (licRes.status === 'fulfilled' && Array.isArray(licRes.value.data)) {
                    licRes.value.data.forEach(l => {
                        docs.push({
                            id: `license-${l.id}`,
                            title: `Biểu mẫu: ${l.name}`,
                            subtitle: l.description || 'Biểu mẫu hành chính công ty',
                            type: 'doc-dynamic',
                            category: 'doc',
                            path: l.link || '/licenses?tab=licenses',
                            icon: ExternalLink,
                            keywords: `bieu mau ${l.name} ${l.description || ''} gpkd giay to`
                        });
                    });
                }

                if (annRes.status === 'fulfilled' && Array.isArray(annRes.value.data)) {
                    annRes.value.data.forEach(a => {
                        docs.push({
                            id: `announcement-${a.id}`,
                            title: `[${a.code}] ${a.title}`,
                            subtitle: a.summary || 'Thông báo văn bản nội bộ',
                            type: 'doc-dynamic',
                            category: 'doc',
                            path: `/licenses?tab=announcements&id=${a.id}`,
                            icon: FileText,
                            keywords: `thong bao van ban ${a.code} ${a.title} quyet dinh thong bao`
                        });
                    });
                }

                setDynamicDocs(docs);
            } catch (err) {
                console.error('Failed to pre-fetch licenses & announcements', err);
            }
        };
        fetchRemoteDocs();
    }, []);

    // Toggle Modal on Cmd+K or Ctrl+K
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setIsOpen((prev) => !prev);
            }
            if (e.key === 'Escape' && isOpen) {
                setIsOpen(false);
            }
        };
        const handleCustomOpen = () => setIsOpen(true);
        
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('open-command-palette', handleCustomOpen);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('open-command-palette', handleCustomOpen);
        };
    }, [isOpen]);

    // Focus input on open
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
            setQuery('');
            setSelectedIndex(0);
        }
    }, [isOpen]);

    // Kiểm tra quyền truy cập của user đối với từng mục
    const isPermitted = (item) => {
        if (currentUser?.role === 'admin') return true;
        if (item.requiredRole && item.requiredRole !== currentUser?.role) return false;
        if (item.requiredPermission) {
            const [domain, action] = item.requiredPermission;
            if (!currentUser?.permissions?.[domain]?.[action]) return false;
        }
        return true;
    };

    // Danh sách ứng viên hợp lệ đã lọc phân quyền
    const candidateItems = useMemo(() => {
        const list = [...actionShortcuts, ...coreModules, ...documentationItems, ...dynamicDocs];
        return list.filter(isPermitted);
    }, [dynamicDocs, currentUser]);

    // Xếp hạng và phân nhóm kết quả (Grouped Ranking)
    const { selectableItems, groupedResults } = useMemo(() => {
        const qTrim = query.trim();
        const qNorm = removeVietnameseTones(qTrim);
        const qTokens = qNorm.split(/\s+/).filter(Boolean);

        let scoredItems = [];

        if (qTokens.length === 0) {
            // Trạng thái mặc định khi chưa gõ: hiển thị hành động nhanh và module thông dụng
            const defaultActions = actionShortcuts.filter(isPermitted).slice(0, 4);
            const defaultModules = coreModules.filter(isPermitted).slice(0, 8);
            const defaultDocs = documentationItems.slice(0, 3);
            
            scoredItems = [...defaultActions, ...defaultModules, ...defaultDocs];
        } else {
            scoredItems = candidateItems
                .map(item => {
                    const tNorm = removeVietnameseTones(item.title);
                    const sNorm = removeVietnameseTones(item.subtitle || '');
                    const kNorm = removeVietnameseTones(item.keywords || '');
                    const idNorm = item.id.toLowerCase();
                    const pathNorm = (item.path || '').toLowerCase();
                    const fullSearchText = `${tNorm} ${sNorm} ${kNorm} ${idNorm} ${pathNorm}`;

                    // Tất cả token phải có mặt trong văn bản
                    const matchesAll = qTokens.every(token => fullSearchText.includes(token));
                    if (!matchesAll) return null;

                    // Tính điểm liên quan (Relevance Scoring)
                    let score = 0;
                    if (tNorm === qNorm) score += 150;
                    else if (tNorm.startsWith(qNorm)) score += 90;
                    else if (tNorm.includes(qNorm)) score += 60;
                    else if (kNorm.includes(qNorm)) score += 40;
                    else if (sNorm.includes(qNorm)) score += 30;

                    // Ưu tiên Thao tác nhanh (Action Shortcuts) lên trên
                    if (item.category === 'action') score += 45;

                    // Thưởng điểm token
                    qTokens.forEach(token => {
                        if (tNorm.includes(token)) score += 18;
                        if (kNorm.includes(token)) score += 10;
                        if (sNorm.includes(token)) score += 5;
                    });

                    return { item, score };
                })
                .filter(Boolean)
                .sort((a, b) => b.score - a.score)
                .map(res => res.item);
        }

        // Phân tách thành 3 nhóm
        const actions = scoredItems.filter(i => i.category === 'action').slice(0, 4);
        const modules = scoredItems.filter(i => i.category === 'module').slice(0, 8);
        const docs = scoredItems.filter(i => i.category === 'doc' || i.type === 'doc' || i.type === 'doc-dynamic' || i.type === 'external').slice(0, 4);

        const flatList = [...actions, ...modules, ...docs];

        return {
            selectableItems: flatList,
            groupedResults: [
                { id: 'actions', title: '⚡ THAO TÁC NHANH', items: actions },
                { id: 'modules', title: '📌 CHỨC NĂNG & MODULE', items: modules },
                { id: 'docs', title: '📚 TÀI LIỆU, SOP & HƯỚNG DẪN', items: docs }
            ].filter(g => g.items.length > 0)
        };
    }, [query, candidateItems]);

    // Keyboard navigation
    useEffect(() => {
        const handleNavigation = (e) => {
            if (!isOpen) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex(prev => (prev < selectableItems.length - 1 ? prev + 1 : prev));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (selectableItems[selectedIndex]) {
                    handleSelect(selectableItems[selectedIndex], e.metaKey || e.ctrlKey);
                }
            }
        };
        window.addEventListener('keydown', handleNavigation);
        return () => window.removeEventListener('keydown', handleNavigation);
    }, [isOpen, selectableItems, selectedIndex]);

    const handleSelect = (item, isNewTab = false) => {
        setIsOpen(false);
        setQuery('');

        // Lưu vào truy cập gần đây
        const safeItem = { 
            id: item.id, 
            title: item.title, 
            type: item.type, 
            category: item.category,
            path: item.path, 
            subtitle: item.subtitle 
        };
        const newRecent = [safeItem, ...recentSearches.filter(r => r.id !== item.id)].slice(0, 5);
        setRecentSearches(newRecent);
        try { localStorage.setItem('cmdPaletteRecent', JSON.stringify(newRecent)); } catch {}

        // Kích hoạt Action qua Event
        if (item.type === 'action' && item.action) {
            window.dispatchEvent(new CustomEvent(item.action));
            return;
        }

        // Mở biểu mẫu động ngoài
        if (item.type === 'doc-dynamic' && item.path && item.path !== '#') {
            if (item.path.startsWith('http')) {
                window.open(item.path, '_blank');
                return;
            }
        }

        // Liên kết ngoài hoặc tab mới
        if (isNewTab || item.type === 'external' || (item.path && item.path.startsWith('http'))) {
            window.open(item.path, '_blank');
        } else if (item.path) {
            navigate(item.path);
            if (onNavigate) {
                // Tách path sạch để lấy baseTab (bỏ query parameters như ?filter=birthday)
                const cleanBaseTab = item.path.split('?')[0].replace(/^\//, '').split('/')[0];
                onNavigate(cleanBaseTab || item.id);
            }
        }
    };

    const highlightMatch = (text, highlight) => {
        if (!highlight || !text) return text;
        const nText = removeVietnameseTones(text);
        const nHighlight = removeVietnameseTones(highlight);
        const index = nText.indexOf(nHighlight);
        if (index === -1) return text;
        
        const matchLen = Math.min(highlight.length, text.length - index);
        return (
            <>
                {text.substring(0, index)}
                <span style={{ backgroundColor: '#fed7aa', color: '#9a3412', padding: '0 2px', borderRadius: '3px', fontWeight: '700' }}>
                    {text.substring(index, index + matchLen)}
                </span>
                {text.substring(index + matchLen)}
            </>
        );
    };

    const getIcon = (item) => {
        if (item.icon) return item.icon;
        const found = candidateItems.find(i => i.id === item.id);
        return found?.icon || Search;
    };

    if (!isOpen) return null;

    // Tính toán index phẳng cho từng item trong group để hover/keyboard khớp chính xác
    let runningIndex = 0;

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            backdropFilter: 'blur(5px)',
            zIndex: 99999,
            display: 'flex', justifyContent: 'center', paddingTop: '10vh'
        }} onClick={() => setIsOpen(false)}>
            <div 
                style={{
                    backgroundColor: '#ffffff',
                    width: '660px',
                    maxWidth: '92%',
                    borderRadius: '16px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
                    overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                    animation: 'fadeIn 0.15s ease-out',
                    maxHeight: '78vh',
                    border: '1px solid #e2e8f0'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Search Header */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#fafaf9' }}>
                    <Search color="#e55e20" size={22} style={{ marginRight: '14px', flexShrink: 0 }} />
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Tìm nghỉ phép, sinh nhật, đặt phòng, SOP, khách hàng, tour..."
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setSelectedIndex(0);
                        }}
                        style={{
                            flex: 1, border: 'none', outline: 'none',
                            fontSize: '16px', color: '#0f172a',
                            backgroundColor: 'transparent', fontWeight: '500'
                        }}
                    />
                    <div style={{ 
                        fontSize: '11px', color: '#64748b', backgroundColor: '#f1f5f9', 
                        padding: '3px 7px', borderRadius: '5px', fontWeight: '700',
                        border: '1px solid #cbd5e1'
                    }}>
                        ESC
                    </div>
                </div>

                {/* Recent Searches */}
                {query.length === 0 && recentSearches.length > 0 && (
                    <div style={{ padding: '10px 18px', borderBottom: '1px solid #f1f5f9', background: '#ffffff' }}>
                        <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Truy cập gần đây</span>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                            {recentSearches.map(r => (
                                <div key={r.id} onClick={() => handleSelect(r)} style={{ fontSize: '12px', padding: '5px 12px', backgroundColor: '#f8fafc', borderRadius: '20px', cursor: 'pointer', color: '#334155', border: '1px solid #e2e8f0', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span>{r.title}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Results List */}
                <div style={{ padding: '8px', maxHeight: '500px', overflowY: 'auto' }}>
                    {selectableItems.length === 0 ? (
                        <div style={{ padding: '36px 20px', textAlign: 'center', color: '#64748b' }}>
                            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>
                                Không tìm thấy kết quả phù hợp cho "{query}"
                            </div>
                            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                                Thử bấm vào các gợi ý tra cứu phổ biến bên dưới:
                            </div>
                            {/* Smart Suggestion Chips */}
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                {smartSuggestionChips.map((chip, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => {
                                            setQuery(chip.query);
                                            setSelectedIndex(0);
                                            if (inputRef.current) inputRef.current.focus();
                                        }}
                                        style={{
                                            border: '1px solid #fed7aa',
                                            backgroundColor: '#fff7ed',
                                            color: '#c2410c',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            padding: '6px 12px',
                                            borderRadius: '20px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            transition: 'all 0.15s ease'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ffedd5'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff7ed'}
                                    >
                                        {chip.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        groupedResults.map((group) => (
                            <div key={group.id} style={{ marginBottom: '12px' }}>
                                {/* Non-selectable Category Header */}
                                <div style={{ 
                                    padding: '6px 12px 4px 12px', 
                                    fontSize: '11px', 
                                    fontWeight: '800', 
                                    color: '#94a3b8', 
                                    letterSpacing: '0.6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    {group.title}
                                </div>

                                {group.items.map((item) => {
                                    const itemIndex = runningIndex++;
                                    const isSelected = itemIndex === selectedIndex;
                                    const IconComponent = getIcon(item);

                                    return (
                                        <div 
                                            key={item.id}
                                            onMouseEnter={() => setSelectedIndex(itemIndex)}
                                            onClick={(e) => handleSelect(item, e.metaKey || e.ctrlKey)}
                                            style={{
                                                display: 'flex', alignItems: 'center', padding: '10px 14px',
                                                backgroundColor: isSelected ? '#fff7ed' : 'transparent',
                                                borderRadius: '10px', cursor: 'pointer',
                                                transition: 'all 0.1s ease', gap: '14px',
                                                marginBottom: '2px'
                                            }}
                                        >
                                            <div style={{ 
                                                backgroundColor: isSelected ? '#ffedd5' : item.category === 'action' ? '#fef3c7' : '#f8fafc',
                                                padding: '9px', borderRadius: '10px', flexShrink: 0,
                                                boxShadow: isSelected ? '0 2px 5px rgba(229, 94, 32, 0.15)' : 'none',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                                            }}>
                                                <IconComponent size={18} color={isSelected ? '#e55e20' : item.category === 'action' ? '#d97706' : '#64748b'} />
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <h4 style={{ margin: 0, fontSize: '14px', color: isSelected ? '#9a3412' : '#1e293b', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {highlightMatch(item.title, query)}
                                                    </h4>
                                                    {item.deptBadge && (
                                                        <span style={{ fontSize: '10px', background: '#f1f5f9', color: '#475569', padding: '1px 6px', borderRadius: '4px', fontWeight: 600, flexShrink: 0 }}>
                                                            {item.deptBadge}
                                                        </span>
                                                    )}
                                                    {item.badge && (
                                                        <span style={{ fontSize: '10px', background: '#eff6ff', color: '#2563eb', padding: '1px 6px', borderRadius: '4px', fontWeight: 600, flexShrink: 0 }}>
                                                            {item.badge}
                                                        </span>
                                                    )}
                                                </div>
                                                {item.subtitle && (
                                                    <div style={{ fontSize: '12px', color: isSelected ? '#7c2d12' : '#64748b', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {highlightMatch(item.subtitle, query)}
                                                    </div>
                                                )}
                                            </div>
                                            {isSelected && (
                                                <div style={{ fontSize: '11px', color: '#ea580c', backgroundColor: '#ffffff', padding: '3px 8px', borderRadius: '6px', border: '1px solid #fed7aa', fontWeight: '600', flexShrink: 0 }}>
                                                    {item.type === 'external' ? 'Mở tab ↗' : '↵ Enter'}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))
                    )}
                </div>
                
                {/* Footer */}
                <div style={{ padding: '10px 20px', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: '#94a3b8' }}>
                    <div style={{ display: 'flex', gap: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ backgroundColor: '#ffffff', padding: '1px 5px', borderRadius: '3px', border: '1px solid #cbd5e1', color: '#475569', fontWeight: '600' }}>↑↓</span> Điều hướng
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ backgroundColor: '#ffffff', padding: '1px 5px', borderRadius: '3px', border: '1px solid #cbd5e1', color: '#475569', fontWeight: '600' }}>↵</span> Chọn
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ backgroundColor: '#ffffff', padding: '1px 5px', borderRadius: '3px', border: '1px solid #cbd5e1', color: '#475569', fontWeight: '600' }}>⌘ ↵</span> Mở Tab mới
                        </div>
                    </div>
                    <span style={{ color: '#cbd5e1' }}>FIT TOUR ERP Quick Search</span>
                </div>
            </div>
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px) scale(0.98); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}</style>
        </div>
    );
};

export default CommandPalette;
